import { redirect } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { getMyTenant, type MyTenant, TENANT_ME_QUERY_KEY } from '@/lib/api/tenants';
import { queryClient } from '@/lib/query/client';

import { loadTokens } from './tokens';

/**
 * React Router data-router loader that gates protected routes on the
 * presence of stored Cognito tokens. Returns `null` when tokens exist
 * (the route may render) or a `redirect` to `/signin` when they don't.
 *
 * **Preserves the original target as `?next=<encoded-path>`** so the
 * signin / signup pages can route the user back after auth. The
 * BC-initiated install flow (ADR 0011) depends on this: BC redirects
 * to `/signup?bc_claim=…`, which on signed-out signin attempts ends
 * up at `/signin?next=/app/bc-claim?id=…`; after signin the user
 * lands at the claim handler instead of `/app` (which would skip the
 * claim).
 *
 * Token *expiry* is not checked here; an expired access token surfaces
 * as a 401 from the API and the SPA-side handler re-routes. Pulling
 * `decodeIdToken` into the loader would add a JWT parse to every
 * navigation for negligible UX gain.
 */
export function requireAuth(args?: LoaderFunctionArgs): Response | null {
  const tokens = loadTokens();
  if (!tokens) {
    return redirect(buildSignInRedirect(args?.request));
  }
  return null;
}

function buildSignInRedirect(request: Request | undefined): string {
  if (!request) return '/signin';
  // Encode the originating path+search (not the host) so /signin can
  // navigate locally after auth. Drop fragment — Mailchute doesn't
  // use hash routing.
  const url = new URL(request.url);
  const next = `${url.pathname}${url.search}`;
  // Don't add ?next= for trivial cases (the signin/signup pages
  // themselves, or the root). Keeps URLs readable when there's
  // nothing meaningful to preserve.
  if (next === '/' || next === '/signin' || next === '/signup') {
    return '/signin';
  }
  const target = new URL('/signin', url.origin);
  target.searchParams.set('next', next);
  return `${target.pathname}${target.search}`;
}

/**
 * Sentinel thrown by `ensureMyTenant` when the API returns 401. The
 * loaders catch it and translate to a `/signin` redirect. Distinct
 * type so we don't accidentally swallow real fetch failures.
 */
class UnauthorisedError extends Error {
  constructor() {
    super('Unauthorised');
    this.name = 'UnauthorisedError';
  }
}

/**
 * Shared helper: ensures the `tenant/me` query is cached and returns
 * the unwrapped Tenant. Both onboarding-state loaders use this so the
 * round trip happens at most once per /app navigation (TanStack Query
 * dedups within `staleTime`).
 *
 * Throws `UnauthorisedError` on 401 (caller converts to `/signin`).
 * Throws a plain `Error` on any other fetch failure — that surfaces as
 * a route error boundary fallback rather than a silent allow-through.
 * A tenant we can't read is a state we don't want to make routing
 * decisions from.
 */
async function ensureMyTenant(): Promise<MyTenant> {
  return queryClient.ensureQueryData({
    queryKey: TENANT_ME_QUERY_KEY,
    queryFn: async () => {
      const result = await getMyTenant();
      if (result.isErr()) {
        if (result.error.kind === 'unauthorised') {
          throw new UnauthorisedError();
        }
        throw new Error(`Failed to load tenant: ${result.error.message}`);
      }
      return result.value;
    },
  });
}

async function ensureMyTenantOrRedirect(
  request: Request | undefined,
): Promise<MyTenant | Response> {
  try {
    return await ensureMyTenant();
  } catch (caught) {
    if (caught instanceof UnauthorisedError) {
      // Drop the cached (failed) query so the next sign-in re-fetches
      // cleanly.
      queryClient.removeQueries({ queryKey: TENANT_ME_QUERY_KEY });
      return redirect(buildSignInRedirect(request));
    }
    throw caught;
  }
}

/**
 * Loader for `/app/*` non-onboarding routes (dashboard etc.).
 *
 * - No tokens → `/signin?next=…` (via requireAuth).
 * - Tokens but `onboarded_at` null → redirect into the wizard at the
 *   first incomplete step.
 * - Tokens and onboarded → allow.
 */
export async function requireOnboarded(args: LoaderFunctionArgs): Promise<Response | null> {
  const authCheck = requireAuth(args);
  if (authCheck) return authCheck;

  const tenantOrRedirect = await ensureMyTenantOrRedirect(args.request);
  if (tenantOrRedirect instanceof Response) return tenantOrRedirect;
  if (!tenantOrRedirect.onboarded_at) {
    return redirect(`/app/onboarding/step/${String(firstIncompleteStep(tenantOrRedirect))}`);
  }
  return null;
}

/**
 * Loader for `/app/onboarding/step/N`.
 *
 * - No tokens → `/signin?next=…` (via requireAuth).
 * - Tokens and `onboarded_at` set → user is past the wizard; redirect
 *   to the dashboard.
 * - Tokens and `onboarded_at` null → allow.
 *
 * The loader deliberately does *not* enforce "no jumping ahead" — the
 * wizard's Back / Continue buttons own intra-wizard navigation. Letting
 * the user URL-jump to a later step they already filled is fine; the
 * form pre-fills from cached tenant data.
 */
export async function requireInOnboarding(args: LoaderFunctionArgs): Promise<Response | null> {
  const authCheck = requireAuth(args);
  if (authCheck) return authCheck;

  const tenantOrRedirect = await ensureMyTenantOrRedirect(args.request);
  if (tenantOrRedirect instanceof Response) return tenantOrRedirect;
  if (tenantOrRedirect.onboarded_at) {
    return redirect('/app');
  }
  return null;
}

/**
 * Map a tenant's completed-fields shape to the lowest incomplete
 * REQUIRED step.
 *
 * Steps 1–3 each correspond to a persisted field (`legal_name`,
 * `default_ship_from_address_id`, an active BC `Connection`). Step 4
 * (carrier) is optional + stateless — there's no completion flag to
 * gate on, so it's never a resume target (the merchant reaches it via
 * the Back button on step 5 if they want to connect a carrier). Step 5
 * (billing) is the final required gate per ADR 0017 — a tenant past
 * step 3 with no Subscription yet (`billing_status` absent or
 * `unbilled`) resumes there.
 *
 * Consulted by `requireOnboarded` to bounce the user back into the
 * wizard if they hit a protected route before finishing onboarding.
 */
export function firstIncompleteStep(tenant: MyTenant): 1 | 2 | 3 | 4 | 5 {
  if (!tenant.legal_name) return 1;
  if (!tenant.default_ship_from_address_id) return 2;
  if (tenant.bc_connection?.status !== 'active') return 3;
  return 5;
}
